import * as React from "react";
import { useNavigate, useLocation } from "react-router";
import { Container } from '@/components/ui/container';
import { Stack, Inline } from '@/components/ui/layout';
import { Heading, Text } from '@/components/ui/typography';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';

function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  // Function to scroll to Testimonial section
  const scrollToTestimonial = () => {
    const testSection = document.getElementById("test-section");
    if (testSection) {
      testSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Handle Testimonial Click: If on home page → Scroll; Else → Navigate to home first
  const handleTestimonialClick = () => {
    if (location.pathname === "/") {
      scrollToTestimonial();
    } else {
      navigate("/");
      setTimeout(scrollToTestimonial, 500);
    }
  };

  const footerLinks = [
    { label: "Home", onClick: () => navigate("/") },
    { label: "About", onClick: () => navigate("/aboutus") },
    { label: "Testimonial", onClick: handleTestimonialClick },
    { label: "Contact", onClick: () => navigate("/contactus") },
  ];

  const socialLinks = [
    { 
      src: "https://cdn.builder.io/api/v1/image/assets/TEMP/5ddd35eef5c69ef0596f66370874e59011c29ffa48ce3d63017325034ecfe8e1?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214",
      label: "Facebook",
      href: "#"
    },
    { 
      src: "https://cdn.builder.io/api/v1/image/assets/TEMP/0c41897b9623272ff9fc21d33e077ddf8b318e9a65bc97b790ffeaa9b2bd20c6?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214",
      label: "Twitter",
      href: "#"
    },
    { 
      src: "https://cdn.builder.io/api/v1/image/assets/TEMP/fcbf4555875ea826f6d9b77b862c9fc6b1f1db1a8a49cb36c3a19a10b090f106?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214",
      label: "Instagram",
      href: "#"
    },
    { 
      src: "https://cdn.builder.io/api/v1/image/assets/TEMP/e895fca23d305a9d293b771cf6140863fcd16dd29446228275008ddd3bece07a?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214",
      label: "YouTube",
      href: "#"
    },
  ];

  return (
    <footer className={cn(
      "bg-primary text-primary-foreground",
      "border-t border-primary/20"
    )}>
      <Container>
        <div className="py-8 md:py-10">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 mb-8">
            {/* Logo and Info Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <img 
                  className="h-10 w-auto object-contain" 
                  src="https://cdn.builder.io/api/v1/image/assets/TEMP/926a85850cee67d788a73327e4e39479fd245168252ab212774d95156da9ff34?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214" 
                  alt="Logo" 
                />
                <div>
                  <div className="text-sm font-bold text-primary-foreground">AI EVALUATOR</div>
                  <div className="text-xs text-primary-foreground/80">INTERVIEW MACHINE</div>
                </div>
              </div>
              <Text size="sm" className="leading-relaxed max-w-md text-primary-foreground/90">
                Harness AI to promote transparency, efficiency, and inclusivity—building a stronger, more diverse workforce.
              </Text>
              <Inline spacing="sm" className="mt-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    className={cn(
                      "inline-flex items-center justify-center",
                      "h-9 w-9 rounded-md",
                      "bg-primary-foreground/10 border border-primary-foreground/20",
                      "hover:bg-primary-foreground/20 hover:border-primary-foreground/30",
                      "transition-all duration-200",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/50 focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                    )}
                  >
                    <img 
                      src={social.src}
                      alt={social.label}
                      className="h-4 w-4 object-contain opacity-80 hover:opacity-100 transition-opacity brightness-0 invert"
                    />
                  </a>
                ))}
              </Inline>
            </div>

            {/* Links Section */}
            <div className="space-y-4">
              <Heading level="h3" className="text-base font-semibold mb-3 text-primary-foreground">
                Quick Links
              </Heading>
              <Separator className="mb-4 bg-primary-foreground/20" />
              <div className="space-y-1">
                {footerLinks.map((link, index) => (
                  <Button
                    key={index}
                    variant="ghost"
                    onClick={link.onClick}
                    className={cn(
                      "w-full justify-start text-left",
                      "text-primary-foreground/80 hover:text-primary-foreground",
                      "h-9 px-0",
                      "text-sm font-normal",
                      "hover:bg-primary-foreground/10",
                      "transition-colors"
                    )}
                  >
                    <ChevronRight className="h-3.5 w-3.5 mr-2 flex-shrink-0 opacity-60" />
                    {link.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
      
      {/* Footer Bottom */}
      <div className={cn(
        "bg-primary/90 border-t border-primary-foreground/10",
        "py-4"
      )}>
        <Container>
          <Text size="sm" className="text-center text-primary-foreground/90">
            © 2025 <span className="font-semibold text-primary-foreground">AI EVALUATOR</span>. All Rights Reserved.
          </Text>
        </Container>
      </div>
    </footer>
  );
}

export default Footer;
